import { HEN_BREEDS, BREED_IDS } from '../data/breeds';
import { ROOSTER_BREEDS } from '../data/roosters';
import { ACHIEVEMENTS } from '../data/achievements';
import { CHALLENGES } from '../data/misc';
import { UPGRADES } from '../data/upgrades';
import type { AchCondition, BreedId, ChallengeDef, Tier } from '../data/types';
import { farmTotals, upgradeCost } from './economy';
import { isFirstOfKind } from './modifiers';
import { reqsMet } from './requirements';
import { countHens, countRoosters, emit, logActivity, ownedBreedCount, ownedBreeds } from './state';
import type { Flock, GameState, SimCtx } from './types';

// ── Condition evaluation ──────────────────────────────────────────────

const flockHens = (f: Flock) => f.boxes.flatMap((b) => (b.hen ? [b.hen] : []));

function flockTierCount(f: Flock, tier: Tier): number {
  return flockHens(f).filter((h) => HEN_BREEDS[h.breed].tier === tier).length;
}

function eggsInWindow(s: GameState, seconds: number): number {
  const b = s.stats.eggBuckets;
  const nowSec = Math.floor(s.time / 1000);
  const lag = nowSec - s.stats.eggBucketSec; // buckets missing at the end
  let sum = 0;
  for (let i = b.length - 1, age = lag; i >= 0 && age < seconds; i--, age++) sum += b[i]!;
  return sum;
}

function almostAfford3(s: GameState): boolean {
  let near = 0;
  for (const def of UPGRADES) {
    if (def.category === 'coop' || s.upgrades.includes(def.id) || !reqsMet(s, def.requires)) continue;
    const cost = upgradeCost(s, def);
    if (cost > s.money && cost <= s.money * 1.05) near++;
    if (near >= 3) return true;
  }
  return false;
}

export function conditionProgress(s: GameState, ctx: SimCtx, c: AchCondition): { current: number; target: number } {
  const flag = (v: boolean) => ({ current: v ? 1 : 0, target: 1 });
  const best = (vals: number[], target: number) => ({ current: vals.length ? Math.max(...vals) : 0, target });
  switch (c.t) {
    case 'eggsCollected': return { current: s.eggsCollected, target: c.n };
    case 'layRate': return { current: farmTotals(s, ctx, true).layRate, target: c.n };
    case 'hens': return { current: countHens(s), target: c.n };
    case 'roosters': return { current: countRoosters(s), target: c.n };
    case 'crows': return { current: s.stats.crows, target: c.n };
    case 'roosterBreedsEver': return { current: s.roosterBreedsEver.length, target: c.n };
    case 'breedTypes': return { current: ownedBreeds(s).size, target: c.n };
    case 'ownAllStarters': {
      const owned = ownedBreeds(s);
      return { current: BREED_IDS.filter((b) => HEN_BREEDS[b].tier === 'STARTER' && owned.has(b)).length, target: 4 };
    }
    case 'ownTier': {
      let n = 0;
      for (const f of s.flocks) n += flockTierCount(f, c.tier);
      return { current: n, target: c.n };
    }
    case 'clicks': return { current: s.stats.clicks, target: c.n };
    case 'goldenClicked': return { current: s.stats.goldenClicked, target: c.n };
    case 'silverClicked': return { current: s.stats.silverClicked, target: c.n };
    case 'rainbowClicked': return { current: s.stats.rainbowClicked, target: c.n };
    case 'broodyHatches': return { current: s.stats.broodyHatches, target: c.n };
    case 'rottenSeen': return { current: s.stats.rottenSeen, target: c.n };
    case 'rottenSalvaged': return { current: s.stats.rottenSalvaged, target: c.n };
    case 'rottenExpired': return { current: s.stats.rottenExpired, target: c.n };
    case 'roosterEggsHatched': return { current: s.stats.roosterEggsHatched, target: c.n };
    case 'eggsInWindow': return { current: eggsInWindow(s, c.seconds), target: c.n };
    case 'lateNight': return flag(ctx.localHour >= 0 && ctx.localHour < 3);
    case 'almostAfford3': return flag(almostAfford3(s));
    case 'ownBreed': return { current: ownedBreedCount(s, c.breed), target: c.n };
    case 'flockAllBroodyProne':
      return best(s.flocks.filter((f) => f.rooster).map((f) => {
        const hens = flockHens(f);
        return hens.every((h) => HEN_BREEDS[h.breed].broodyProne) ? hens.length : 0;
      }), c.n);
    case 'flockNoneBroodyProne':
      return best(s.flocks.filter((f) => f.rooster).map((f) => {
        const hens = flockHens(f);
        return hens.every((h) => !HEN_BREEDS[h.breed].broodyProne) ? hens.length : 0;
      }), c.n);
    case 'flockDistinctBreeds': return best(s.flocks.map((f) => new Set(flockHens(f).map((h) => h.breed)).size), c.n);
    case 'flockTier': return best(s.flocks.map((f) => flockTierCount(f, c.tier)), c.n);
    case 'homogeneousFlocks': {
      const breeds = new Set<BreedId>();
      for (const f of s.flocks) {
        const kinds = new Set(flockHens(f).map((h) => h.breed));
        if (kinds.size === 1) breeds.add([...kinds][0]!);
      }
      return { current: breeds.size, target: c.n };
    }
    case 'postWinFlockTier': return s.won ? best(s.flocks.map((f) => flockTierCount(f, c.tier)), c.n) : { current: 0, target: c.n };
    case 'concurrentBroody': return { current: s.events.filter((e) => e.kind === 'broody').length, target: c.n };
    case 'roosterWithAllBroodyProne':
      return best(s.flocks.filter((f) => f.rooster?.breed === c.rooster).map((f) => {
        const hens = flockHens(f);
        return hens.every((h) => HEN_BREEDS[h.breed].broodyProne) ? hens.length : 0;
      }), c.n);
    case 'roostersWithin': {
      const walls = s.stats.roosterWalls;
      const within = walls.filter((w) => w - s.runStartWall <= c.seconds * 1000).length;
      return { current: Math.min(within, countRoosters(s)), target: c.n };
    }
    case 'activePlay': return { current: s.stats.activePlaySec, target: c.seconds };
    case 'lifetimeActivePlay': return { current: s.stats.lifetimeActivePlaySec, target: c.seconds };
    case 'matchedPair':
      return best(s.flocks.filter((f) => f.rooster?.breed === c.breed).map((f) => flockHens(f).filter((h) => h.breed === c.breed).length), c.n);
    case 'matchedPairFlocks':
      return { current: s.flocks.filter((f) => f.rooster && flockHens(f).some((h) => h.breed === f.rooster!.breed)).length, target: c.n };
    case 'uniqueRoosterBreedsInFlocks':
      return { current: new Set(s.flocks.flatMap((f) => (f.rooster ? [f.rooster.breed] : []))).size, target: c.n };
  }
}

export function conditionMet(s: GameState, ctx: SimCtx, c: AchCondition): boolean {
  const p = conditionProgress(s, ctx, c);
  return p.current >= p.target;
}

/** Awards newly met achievements. Returns their ids. */
export function checkAchievements(s: GameState, ctx: SimCtx): string[] {
  const fresh: string[] = [];
  for (const a of ACHIEVEMENTS) {
    if (s.achievements.includes(a.id)) continue;
    if (!conditionMet(s, ctx, a.condition)) continue;
    s.achievements.push(a.id);
    fresh.push(a.id);
    emit(s, { kind: 'achievement', id: a.id });
    logActivity(s, 'milestone', 'Achievement', a.name);
  }
  return fresh;
}

// ── Challenges (post-win) ─────────────────────────────────────────────

export function challengeProgress(s: GameState, def: ChallengeDef): { current: number; target: number } {
  switch (def.progress) {
    case 'lifetimeEarned': {
      const next = def.tiers?.find((t) => s.lifetimeEarned < t.target) ?? def.tiers?.[def.tiers.length - 1];
      return { current: s.lifetimeEarned, target: next?.target ?? 0 };
    }
    case 'uniqueHenBreeds': return { current: ownedBreeds(s).size, target: BREED_IDS.length };
    case 'uniqueRoosterBreeds': return { current: s.roosterBreedsEver.length, target: Object.keys(ROOSTER_BREEDS).length };
    case 'fullFlocks': return { current: s.flocks.filter((f) => flockHens(f).length >= 15).length, target: def.target ?? 5 };
    case 'firstOfKindBreeds': {
      const set = new Set<BreedId>();
      for (const f of s.flocks) for (const h of flockHens(f)) if (isFirstOfKind(f, h)) set.add(h.breed);
      return { current: set.size, target: BREED_IDS.length };
    }
    case 'achievements': return { current: s.achievements.length, target: ACHIEVEMENTS.length };
  }
}

/** Number of tiers (or 1 for single-target challenges) reached. */
export function challengeTiersReached(s: GameState, def: ChallengeDef): number {
  if (def.tiers) return def.tiers.filter((t) => s.lifetimeEarned >= t.target).length;
  const p = challengeProgress(s, def);
  return p.current >= p.target ? 1 : 0;
}

export function checkChallenges(s: GameState): void {
  if (!s.won) return;
  for (const def of CHALLENGES) {
    const reached = challengeTiersReached(s, def);
    const prev = s.challengeTiers[def.id] ?? 0;
    if (reached > prev) {
      s.challengeTiers[def.id] = reached;
      const label = def.tiers ? def.tiers[reached - 1]!.label : 'Complete';
      emit(s, { kind: 'challengeTier', id: def.id, label });
      logActivity(s, 'milestone', `Challenge: ${def.name}`, label);
    }
  }
}
